import React, { useState } from "react";
import CTAButton from "./CTAButton";
import { getCtaVariant } from "../../lib/ab";
import FormConsent from "./FormConsent";

const FIELDS = [
  { name: "name", label: "Your name", type: "text", placeholder: "Full name", required: true },
  { name: "restaurant", label: "Venue / Brand", type: "text", placeholder: "e.g. Lune Croissanterie", required: true },
  { name: "email", label: "Email", type: "email", placeholder: "you@venue.com.au", required: true },
  { name: "phone", label: "Phone", type: "tel", placeholder: "0412 345 678", required: true },
];

const PROJECT_TYPES = [
  "Menu Photography",
  "Food & Beverage",
  "Venue & Interior",
  "Brand Campaign",
  "Editorial / Cookbook",
  "Other",
];

export const QuoteForm = ({ dark = false, compact = false, source = "quote-form" }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", restaurant: "", email: "", phone: "", message: "" });

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const variant = getCtaVariant();
      const res = await fetch(`${API}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          source,
          project_type: active || null,
          cta_variant: variant.id,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      console.error("Enquiry submit failed", err);
      setError("Something went wrong sending the enquiry. Please call 0494 756 111 directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const palette = dark
    ? {
        text: "text-bone",
        sub: "text-bone/60",
        placeholder: "placeholder:text-bone/40",
        label: "text-bone/70",
        border: "border-bone/20",
        focus: "focus:border-ember",
        bg: "bg-transparent",
        chip: "border-bone/20 text-bone/75 hover:border-bone hover:text-bone",
        chipActive: "border-ember bg-ember text-bone",
      }
    : {
        text: "text-ink",
        sub: "text-char-mute",
        placeholder: "placeholder:text-char-mute/60",
        label: "text-char/70",
        border: "border-ink/15",
        focus: "focus:border-ember",
        bg: "bg-transparent",
        chip: "border-ink/15 text-char hover:border-ink hover:text-ink",
        chipActive: "border-ember bg-ember text-bone",
      };

  if (submitted) {
    return (
      <div
        data-testid="quote-form-success"
        className={`p-10 lg:p-14 border ${palette.border} ${palette.text}`}
      >
        <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
          Enquiry received
        </p>
        <h3 className="font-display text-3xl lg:text-4xl tracking-editorial leading-tight mb-4">
          Thanks — we&apos;ll be in touch within one business day.
        </h3>
        <p className={`text-sm leading-relaxed ${palette.sub} max-w-md`}>
          Most shoots are booked 2–4 weeks ahead. If your launch is sooner, mention
          the date in your reply and we&apos;ll prioritise availability.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className={`${palette.text}`}
    >
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
        {FIELDS.map((f) => (
          <label key={f.name} className="block group">
            <span
              className={`text-xs uppercase tracking-wider2 font-bold ${palette.label} block mb-2`}
            >
              {f.label}
            </span>
            <input
              type={f.type}
              name={f.name}
              required={f.required}
              placeholder={f.placeholder}
              value={form[f.name]}
              onChange={(e) => setField(f.name, e.target.value)}
              data-testid={`quote-${f.name}`}
              className={`w-full ${palette.bg} border-b ${palette.border} ${palette.focus} ${palette.text} ${palette.placeholder} font-display text-2xl tracking-editorial pb-3 focus:outline-none transition-colors`}
            />
          </label>
        ))}
      </div>

      {!compact && (
        <div className="mt-12">
          <p
            className={`text-xs uppercase tracking-wider2 font-bold ${palette.label} mb-4`}
          >
            Project type
          </p>
          <div className="flex flex-wrap gap-2">
            {PROJECT_TYPES.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setActive(t)}
                data-testid={`quote-type-${t.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={`px-4 py-2 text-[11px] uppercase tracking-wider2 border transition-all duration-300 ${
                  active === t ? palette.chipActive : palette.chip
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="block mt-12">
        <span
          className={`text-xs uppercase tracking-wider2 font-bold ${palette.label} block mb-2`}
        >
          Tell us about your shoot
        </span>
        <textarea
          rows={3}
          name="message"
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          placeholder="Dishes, timing, intended use, anything that helps us scope quickly."
          data-testid="quote-message"
          className={`w-full ${palette.bg} border-b ${palette.border} ${palette.focus} ${palette.text} font-sans text-base leading-relaxed pb-3 focus:outline-none transition-colors resize-none`}
        />
      </label>

      {error && (
        <p data-testid="quote-error" className="mt-6 text-xs text-ember font-bold uppercase tracking-wider2">
          {error}
        </p>
      )}

      <div className="mt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <p className={`text-xs ${palette.sub} max-w-sm leading-relaxed`}>
          No spam. Quotes are written by hand, usually within a day. Pricing is
          transparent and based on usage.
        </p>
        <CTAButton type="submit" variant="primary" size="md" disabled={submitting}>
          {submitting ? "Sending…" : "Send Enquiry"}
        </CTAButton>
      </div>

      <FormConsent theme={dark ? "dark" : "light"} className="mt-6" />
    </form>
  );
};

export default QuoteForm;
